import React from 'react';
import { Alert, Box, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconAlertCircle } from '@tabler/icons-react';
import {
    FillOptions,
    pickPatientSignatureFieldName,
    isStaffSignatureFieldName,
} from '../../../global/helpers/eicfFillablePdf.ts';
import {
    EICF_FIELD_ROLES,
    FieldConfigMap,
} from '../../../global/entities/EICFTemplates/fieldConfig.ts';
import { useFillablePdfSigning } from '../../../global/component/eicf/useFillablePdfSigning.ts';
import { uploadFileToS3 } from '../../../global/helpers/uploadFileToS3.ts';
import { EICFConsentsApi } from '../../../global/apis/EICFApi/EICFConsentsApi.ts';
import SignatureAdoptModal from '../../../global/component/SignatureAdoptModal/SignatureAdoptModal.tsx';
import { useSubmitEICFPatientSignature } from '../../../global/entities/EICFConsents/mutations/useSubmitEICFPatientSignature.tsx';
import { IEICFConsent } from '../../../global/entities/EICFConsents/EICFConsent.ts';

interface EICFFillablePdfSignerProps {
    consent: IEICFConsent;
    onSigned: () => void | Promise<void>;
}

// If the clinician has fields to fill, keep the form un-flattened and lock
// everything except the HCP fields, so the HCP can complete them before the
// final flatten happens at counter-signature.
const resolvePatientFillOptions = (
    config: FieldConfigMap,
    placedFieldNames: string[],
): FillOptions => {
    const hasHcpFields = Object.values(config).some(
        (rule) => rule.role === EICF_FIELD_ROLES.Hcp,
    );
    return {
        flatten: !hasHcpFields,
        readOnly: hasHcpFields
            ? [
                  ...Object.entries(config)
                      .filter(([, rule]) => rule.role !== EICF_FIELD_ROLES.Hcp)
                      .map(([name]) => name),
                  ...placedFieldNames,
              ]
            : [],
    };
};

const EICFFillablePdfSigner: React.FC<EICFFillablePdfSignerProps> = ({
    consent,
    onSigned,
}) => {
    const { t } = useTranslation();
    const submitSignature = useSubmitEICFPatientSignature();

    const signing = useFillablePdfSigning({
        sourceUrl: consent.templateDocumentUrl,
        fieldConfigJson: consent.templateFieldConfigJson,
        editableRole: EICF_FIELD_ROLES.Patient,
        pickSignatureField: (sigNames) => pickPatientSignatureFieldName(sigNames),
        isOtherPartySignature: isStaffSignatureFieldName,
        requireSignaturePlacement: true,
        resolveFillOptions: resolvePatientFillOptions,
        submit: async (signedBytes) => {
            const file = new File([signedBytes], 'consent.pdf', {
                type: 'application/pdf',
            });
            const policy = await EICFConsentsApi.getSignedPdfUploadUrl(
                consent.id,
                encodeURIComponent(file.name),
            );
            await uploadFileToS3(policy, file);
            await submitSignature.mutateAsync({
                consentID: consent.id,
                answersJson: '',
                signedPdfS3Key: policy.key,
            });
        },
        onSigned,
        watch: [consent.templateDocumentUrl],
    });

    return (
        <Stack gap="md">
            <Text fw={600}>{t('eicf.pdf.fillInstructions')}</Text>
            {signing.error && (
                <Alert
                    icon={<IconAlertCircle size="1rem" />}
                    color="red"
                    title={t('eicf.page.errorTitle')}
                >
                    {signing.error}
                </Alert>
            )}
            {signing.loading && (
                <Group>
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        {t('eicf.pdf.loading')}
                    </Text>
                </Group>
            )}

            <Box
                ref={signing.pagesRef}
                style={{ maxHeight: '75vh', overflow: 'auto' }}
            />

            <Group justify="flex-end">
                <Button
                    onClick={signing.handleSubmit}
                    loading={signing.submitting}
                    disabled={signing.loading || !signing.signaturePlaced}
                >
                    {t('eicf.pdf.signAndSubmit')}
                </Button>
            </Group>

            <SignatureAdoptModal
                opened={signing.modalOpen}
                onClose={signing.closeModal}
                defaultName={consent.patientName ?? ''}
                onAdopt={signing.handleAdopt}
            />
        </Stack>
    );
};

export default EICFFillablePdfSigner;
